import { useWatch } from 'react-hook-form-v7';
import type { Control } from 'react-hook-form-v7';

import { VerificationStatus } from '@personnel/domain/types';
import type { VerificationStatusType } from '@personnel/domain/types';
import type { EmployeeFormData } from '@personnel/presentation/types';

/**
 * The badge state of one verification requirement.
 *
 * A requirement the response never mentioned has nothing to say, so it paints no badge: an absent
 * key is not the same as a pending one.
 *
 * @param isVerified - The flag the requirement map published for that value
 */
export const toVerificationStatus = (isVerified?: boolean): VerificationStatusType | undefined => {
  if (isVerified === undefined) return undefined;

  return isVerified ? VerificationStatus.VERIFIED : VerificationStatus.NOT_VERIFIED;
};

/**
 * The badge state for the phone field, read from the form itself.
 *
 * It never recalculates: the number is not edited here, so what arrived is what is true. Creation
 * has no badge, and neither has a record without a number.
 *
 * @param control - The employee form control
 * @returns The state to paint, or undefined when there is nothing to say
 */
export const usePhoneVerificationBadge = (control: Control<EmployeeFormData>): VerificationStatusType | undefined => {
  const isEditMode = useWatch({ control, name: '_isEditMode' });
  const phoneNumber = useWatch({ control, name: 'phoneNumber' });
  const requirements = useWatch({ control, name: '_verificationRequirements' });

  if (!isEditMode || !phoneNumber) return undefined;

  return toVerificationStatus(requirements?.PHONE);
};
